import BASE_URL from '../../config/env';
import {apiClient} from '../apiClient';

import {
  IAIReviewJobGroupsResponse,
  ICreateAIReviewJobGroupPayload,
  ICreateAIReviewJobGroupResponse,
  IDeleteAIReviewJobGroupPayload,
  IUpdateAIReviewJobGroupPayload,
  IUpdateAIReviewJobGroupResponse,
  IAIReviewProtocolIntelligencePayload,
  IDeleteAIReviewJobGroupResponse,
  IGetAIReviewSourceTypeResponse,
  ICreateAIReviewJobPayload,
  ICreateAIReviewJobResponse,
  ISubmitAIReviewJobPayload,
  ISubmitAIReviewJobResponse,
  IGetAIReviewJobDetailsPayload,
  IGetAIReviewJobDetailsResponse,
  IUpdateAIReviewJobPayload,
  IUpdateAIReviewJobResponse,
  AIReviewCreateProtocolIntelligencePayload,
  IAIReviewJobsExportRequest,
  IAIReviewJobsExportResponse,
  IExportAIReviewJobPdfPayload,
  IGetAIReviewJobResultResponse,
  IGetAIReviewJobResultPayload,
  IDeleteAIReviewJobPayload,
  IDeleteAIReviewJobResponse,
  IRetryAIReviewJobPayload,
  IRetryAIReviewJobResponse,
  IAddJobToGroupPayload,
  IAddJobToGroupResponse,
  IAIReviewEditJobNamePayload,
  IAIReviewEditJobNameResponse,
  IRemoveJobFromGroupPayload,
  IRemoveJobFromGroupResponse,
  IGetAIReviewDocErrorPanelPayload,
  IAIReviewDocErrorPanelResponse,
  IAIReviewJobResultDocsListPayload,
  IAIReviewJobResultDocsListResponse,
  IAIReviewJobResultRelevancyListPayload,
  IAIReviewJobResultRelevancyListResponse,
  IUpdateProtocolIntelligenceDetailsPayload,
  IGetJobListPayload,
  IGetJobListResponse,
  IGetGroundTruthEvaluationResponse,
  IGetGroundTruthTagsResponse,
  IDeleteAIProtocolIntelligencePayload,
  IDeleteAIProtocolIntelligenceResponse,
  ProtocolVersionsResponse,
  ProtocolIntelligenceListRequest,
  GenerateProtocolArtifactsPayload,
  GenerateProtocolArtifactsResponse,
  ProtocolDetailsResponse,
  ProtocolIntelligenceFileUploadResponse,
  ICreateNewProtocolPayloadProps,
  ICreateNewProtocolResponse,
  ISaveProtocolAsNewPayload,
  ISaveProtocolAsNewResponse,
  IValidateProtocolNameRequestPayload,
  IValidateProtocolNameResponse,
  IUpdateProtocolNamePayload,
  IUpdateProtocolNameResponse,
  IGetMasterDocIdForRelevancyPayload,
  IGetAIReviewCloneHistoryResponse,
  IGetMasterDocIdForIssueCodingPayload,
  IGetMasterDocIdForIssueCodingResponse,
  IGetMasterDocIdForRelevancyAndDocResponse,
  IGetDocListMasterIdJobDocsPayload,
  UpdateProtocolActiveStatePayload,
  GetProtocolVersionDetailsPayloadV2,
  ProtocolVersionDetailsResponseV2,
  UpdateProtocolVersionDetailsPayloadV2,
  UpdateProtocolVersionDetailsResponseV2,
  GetProtocolVersionsPayload,
  GetProtocolVersionsResponse,
  GetProtocolNamesResponse,
  ICreateProtocolIntelligencePayload,
  ICreateProtocolIntelligenceResponse,
  ICreateProtocolVersionPayload,
  ICreateProtocolVersionResponse,
  IUploadProtocolPayload,
  IUploadProtocolResponse,
  GetProtocolVersionHistoryPayload,
  ProtocolVersionHistoryResponse,
  FileUploadResponse,
  AIReviewJobType,
  IDownloadAIReviewJobResultsPayload,
  IAIReviewJobResultIssueCodePayload,
  IAIReviewJobResultIssueCodeResponse,
  IAIReviewRelevancyDocSummaryRequest,
  IAIReviewRelevancyDocSummaryResponse,
  IAIReviewRelevancyDocSummary,
} from '../types/aiReview.type';
import {mapGroundTruthEvaluation} from '../../utils/mapGroundTruthEvaluation';
import {
  normalizeRelevancyDocResultResponse,
  normalizeRelevancyDocSummaryResponse,
} from '../utils/relevancyResponseNormalizer';
import {getJobResultMockResponse} from '../../components/ai/review/job-results/GetJobResultMockResponse';
import {
  BackendErrorData,
  parseApiErrorWithLocalization,
} from '../../utils/custom-errors/parseApiError';
import {AxiosError} from 'axios';

const aiReviewEndpoint = (path: string | number) =>
  `${BASE_URL}/ai/api/v1/review/${path}`;

const jobResultsDownloadPath = (
  jobId: number | string,
  jobType: AIReviewJobType
): string => {
  switch (jobType) {
    case 'ISSUECODE':
      return `jobs/${jobId}/issue-codings/doc-results/download`;
    case 'RELEVANCY':
      return `jobs/${jobId}/relevancy/doc-results/download`;
    default: {
      const unsupported: never = jobType;
      throw new Error(
        `Unsupported job type for job results download: ${unsupported}`
      );
    }
  }
};

export const getAIReviewJobGroups =
  async (): Promise<IAIReviewJobGroupsResponse> => {
    const {data} = await apiClient.get(aiReviewEndpoint('all_job-groups'));
    return data;
  };

export const createAIReviewJobGroup = async (
  payload: ICreateAIReviewJobGroupPayload
): Promise<ICreateAIReviewJobGroupResponse> => {
  const {data} = await apiClient.post(aiReviewEndpoint('job-groups'), payload);
  return data;
};

export const updateAIReviewJobGroup = async (
  payload: IUpdateAIReviewJobGroupPayload
): Promise<IUpdateAIReviewJobGroupResponse> => {
  const {jobGroupId, name} = payload;
  const {data} = await apiClient.put(
    aiReviewEndpoint(`job-groups/${jobGroupId}`),
    {name}
  );
  return data;
};

export const deleteAIReviewJobGroup = async (
  payload: IDeleteAIReviewJobGroupPayload
): Promise<IDeleteAIReviewJobGroupResponse> => {
  const {jobGroupId} = payload;
  const {data} = await apiClient.delete(
    aiReviewEndpoint(`job-groups?jobGroupId=${jobGroupId}`)
  );
  return data;
};

// ================ Protocol intelligence list (POST) ================
const DEFAULT_PROTOCOL_LIST_REQUEST: ProtocolIntelligenceListRequest = {
  sortingColumn: 'ReviewProtocolID',
  direction: 'desc',
  displayLength: 25,
  pageNumber: 1,
  columnFilter: [],
};

const normalizeProtocolListRequest = (
  params?: ProtocolIntelligenceListRequest | string
): ProtocolIntelligenceListRequest => {
  if (typeof params === 'string') {
    const searchText = params.trim();
    return {
      ...DEFAULT_PROTOCOL_LIST_REQUEST,
      displayLength: 100,
      columnFilter: searchText
        ? [{columnName: 'ProtocolName', value: searchText}]
        : [],
    };
  }

  return params ?? DEFAULT_PROTOCOL_LIST_REQUEST;
};

export const getAllProtocolIntelligenceList = async (
  params?: ProtocolIntelligenceListRequest | string
): Promise<ProtocolVersionsResponse> => {
  const request = normalizeProtocolListRequest(params);
  const {data} = await apiClient.post(
    aiReviewEndpoint('protocol-intelligence/list'),
    request
  );

  return {
    totalRecords: data?.totalRecords ?? data?.protocolVersions?.length ?? 0,
    protocolVersions: Array.isArray(data?.protocolVersions)
      ? data.protocolVersions
      : [],
  };
};

export const generateProtocolArtifacts = async ({
  protocolId,
}: GenerateProtocolArtifactsPayload): Promise<GenerateProtocolArtifactsResponse> => {
  const payload = {protocolId};
  const {status = 'ok'} = await apiClient.put(
    aiReviewEndpoint('protocol-intelligence/generate'),
    payload
  );
  return {status};
};

export const getProtocolIntelligenceDetails = async (
  payload: IAIReviewProtocolIntelligencePayload
): Promise<ProtocolDetailsResponse> => {
  const {protocolId} = payload;
  const {data = null} = await apiClient.get(
    aiReviewEndpoint(`protocol-intelligence/${protocolId}`)
  );
  return data;
};

export const getProtocolVersionHistory = async (
  payload: GetProtocolVersionHistoryPayload
): Promise<ProtocolVersionHistoryResponse> => {
  const {data} = await apiClient.get<ProtocolVersionHistoryResponse>(
    aiReviewEndpoint(
      `protocol-intelligence/version-history/${payload.protocolId}`
    )
  );
  if (!Array.isArray(data)) {
    throw new Error('Invalid protocol version history response');
  }
  return data;
};

export const uploadProtocolIntelligence = async (
  payload: AIReviewCreateProtocolIntelligencePayload
): Promise<ProtocolIntelligenceFileUploadResponse> => {
  let requestData: any = payload;
  let headers = {};
  const hasFile = Object.values(payload).some((value) => value instanceof File);
  if (hasFile) {
    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value as any);
      }
    });
    requestData = formData;
    headers = {
      'Content-Type': 'multipart/form-data',
    };
  }
  const {data} = await apiClient.post(
    aiReviewEndpoint('protocol-intelligence/upload'),
    requestData,
    {headers}
  );
  return data;
};

export const updateProtocolActiveState = async ({
  protocolId,
  isActive,
}: UpdateProtocolActiveStatePayload): Promise<any> => {
  const payload = {isActive};
  const {status = 'ok'} = await apiClient.patch(
    aiReviewEndpoint(`protocol-intelligence/${protocolId}/status`),
    payload
  );
  return {status};
};

export const approveProtocolIntelligence = async ({
  protocolId = 0,
}: IAIReviewProtocolIntelligencePayload) => {
  const {data} = await apiClient.put(
    aiReviewEndpoint(`protocol-intelligence/${protocolId}/approve`)
  );
  return data;
};

export const updateProtocolIntelligenceDetails = async (
  payload: IUpdateProtocolIntelligenceDetailsPayload
) => {
  const {protocolId, ...rest} = payload;
  // Remove 'id' field before sending to API
  const body = {
    ...rest,
    issueCodes: rest.issueCodes.map(({question, code}) => ({code, question})),
  };
  const {data} = await apiClient.put(
    aiReviewEndpoint(`protocol-intelligence/${protocolId}`),
    body
  );
  return data;
};

// ================ Protocol Intelligence V2 (do not alter V1 helpers above) ================

export const getProtocolNames = async (): Promise<GetProtocolNamesResponse> => {
  const {data} = await apiClient.get<GetProtocolNamesResponse>(
    aiReviewEndpoint('protocol-intelligence/names')
  );
  return Array.isArray(data) ? data : [];
};

export const getProtocolVersionDetails: (
  payload: GetProtocolVersionDetailsPayloadV2
) => Promise<ProtocolVersionDetailsResponseV2> = async ({versionId}) => {
  const {data} = await apiClient.get<ProtocolVersionDetailsResponseV2>(
    aiReviewEndpoint(`protocol-intelligence/versions/${versionId}`)
  );
  return data;
};

export const getProtocolVersions = async ({
  protocolId,
}: GetProtocolVersionsPayload): Promise<GetProtocolVersionsResponse> => {
  const {data} = await apiClient.get<GetProtocolVersionsResponse>(
    aiReviewEndpoint(`protocol-intelligence/${protocolId}/versions`)
  );
  return data;
};

export const updateProtocolVersionDetails = async ({
  versionId,
  ...body
}: UpdateProtocolVersionDetailsPayloadV2): Promise<UpdateProtocolVersionDetailsResponseV2> => {
  const {data} = await apiClient.put<
    UpdateProtocolVersionDetailsResponseV2 & {protocolVersionId?: number}
  >(aiReviewEndpoint(`protocol-intelligence/${versionId}`), body);
  return {
    protocolId: data.protocolId,
    versionId: data.versionId ?? data.protocolVersionId ?? versionId,
  };
};

export const getAIReviewSourceType =
  async (): Promise<IGetAIReviewSourceTypeResponse> => {
    try {
      return await apiClient.get(aiReviewEndpoint('sourceTypes'));
    } catch (error) {
      console.warn('Unable to get source type', error);
    }
    return {id: 0, sourceType: 'IssueCode'};
  };

export const createAIReviewJob = async (
  payload: ICreateAIReviewJobPayload
): Promise<ICreateAIReviewJobResponse> => {
  const {data} = await apiClient.post(aiReviewEndpoint('jobs'), payload);
  return data;
};

export const updateAIReviewJob = async (
  payload: IUpdateAIReviewJobPayload
): Promise<IUpdateAIReviewJobResponse> => {
  const {jobId, ...body} = payload;
  const {data} = await apiClient.put(aiReviewEndpoint(`jobs/${jobId}`), body);
  return data;
};

export const submitAIReviewJob = async (
  payload: ISubmitAIReviewJobPayload
): Promise<ISubmitAIReviewJobResponse> => {
  const body: ICreateAIReviewJobPayload = {
    ...payload,
    status: 'Submitted',
  };
  const {data} = await apiClient.post(aiReviewEndpoint('jobs'), body);
  return data;
};

export const updateAIReviewJobName = async (
  payload: IAIReviewEditJobNamePayload
): Promise<IAIReviewEditJobNameResponse> => {
  const {jobId, ...body} = payload;
  try {
    const {data} = await apiClient.put(
      aiReviewEndpoint(`jobs/editjobname/${jobId}`),
      body
    );
    return data;
  } catch (error) {
    console.warn('Unable to update Job Name', error);
  }
  return {
    jobId: '1',
    name: 'Updated Job Name',
    updatedBy: 'currentUser',
    updatedAt: '2026-04-10T10:15:30Z',
  };
};

export const getAIReviewJobDetails = async (
  payload: IGetAIReviewJobDetailsPayload
): Promise<IGetAIReviewJobDetailsResponse> => {
  const {jobId} = payload;
  const {data} = await apiClient.get(aiReviewEndpoint(`jobs/${jobId}`));
  return data;
};

export const exportAIReviewJobs = async (
  payload: IAIReviewJobsExportRequest
): Promise<IAIReviewJobsExportResponse> => {
  const {data} = await apiClient.post<IAIReviewJobsExportResponse>(
    aiReviewEndpoint('jobs/export'),
    payload
  );
  return data;
};

export const getAIReviewJobDetailData = async (
  payload: IExportAIReviewJobPdfPayload
): Promise<IGetAIReviewJobDetailsResponse> => {
  const {jobId} = payload;
  const {data} = await apiClient.get(aiReviewEndpoint(`jobs/${jobId}`));

  return data;
};

export const deleteAIReviewJob = async (
  payload: IDeleteAIReviewJobPayload
): Promise<IDeleteAIReviewJobResponse> => {
  const {jobId} = payload;
  const {data} = await apiClient.delete(aiReviewEndpoint(`jobs/${jobId}`));
  return data;
};

export const retryAIReviewJob = async (
  payload: IRetryAIReviewJobPayload
): Promise<IRetryAIReviewJobResponse> => {
  const {jobId} = payload;
  const {data} = await apiClient.post(aiReviewEndpoint(`jobs/${jobId}/retry`));
  return data;
};

export const addJobToGroup = async (
  payload: IAddJobToGroupPayload
): Promise<IAddJobToGroupResponse> => {
  const {groupId, jobId, addedBy, reason} = payload;
  const {data} = await apiClient.post(
    aiReviewEndpoint(`job-groups/${groupId}/jobs`),
    {
      jobId,
      ...(addedBy ? {addedBy} : {}),
      ...(reason ? {reason} : {}),
    }
  );
  return data;
};

export const removeJobFromGroup = async (
  payload: IRemoveJobFromGroupPayload
): Promise<IRemoveJobFromGroupResponse> => {
  const {groupId, jobId} = payload;
  const {data} = await apiClient.delete(
    aiReviewEndpoint(`job-groups/${groupId}/jobs/${jobId}`)
  );
  return data;
};

export const getAIReviewJobResult = async (
  payload: IGetAIReviewJobResultPayload
): Promise<IGetAIReviewJobResultResponse> => {
  const {jobId, evaluationType = 'issue-code'} = payload;
  const evaluationPath =
    evaluationType === 'relevancy'
      ? 'results/relevancy-evaluation'
      : 'results/issue-code-evaluation';

  const {data} = await apiClient.get(
    aiReviewEndpoint(`jobs/${jobId}/${evaluationPath}`)
  );
  return data;
};

export const getAIReviewJobResultForExport = async (
  payload: IGetAIReviewJobResultPayload
): Promise<IGetAIReviewJobResultResponse> => {
  const {jobId, evaluationType = 'issue-code'} = payload;
  const evaluationPath =
    evaluationType === 'relevancy'
      ? 'results/relevancy-evaluation'
      : 'results/issue-code-evaluation';
  const {data} = await apiClient.get(
    aiReviewEndpoint(`jobs/${jobId}/${evaluationPath}`)
  );
  return data;
};

export const getAIReviewDocErrorPanelList = async (
  payload: IGetAIReviewDocErrorPanelPayload
): Promise<IAIReviewDocErrorPanelResponse> => {
  const {data} = await apiClient.post(
    aiReviewEndpoint(`documents/errors`),
    payload
  );
  return data;
};

export const downloadAIReviewJobResults = async ({
  jobId,
  jobType,
}: IDownloadAIReviewJobResultsPayload): Promise<string> => {
  try {
    const response = await apiClient.get(
      aiReviewEndpoint(jobResultsDownloadPath(jobId, jobType))
    );
    return response.data;
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'aiReview'
    );
  }
};

export const getAIReviewJobResultIssueCodeList = async (
  jobId: number | string,
  payload: IAIReviewJobResultIssueCodePayload
): Promise<IAIReviewJobResultIssueCodeResponse> => {
  const {data} = await apiClient.post(
    aiReviewEndpoint(`${jobId}/issuecoding/doc-result/view-by-issuecodes`),
    payload
  );
  return normalizeIssueCodeDocResultResponse(data);
};

const normalizeIssueCodeDocResultResponse = (
  data: unknown
): IAIReviewJobResultIssueCodeResponse => {
  const payload = (data ?? {}) as {
    issueCodeResults?: IAIReviewJobResultIssueCodeResponse['issueCodeResults'];
    issuecodeResults?: IAIReviewJobResultIssueCodeResponse['issueCodeResults'];
  };
  const results = payload.issueCodeResults ?? payload.issuecodeResults;
  return {
    issueCodeResults: Array.isArray(results) ? results : [],
  };
};

export const getAIReviewJobResultDocsList = async (
  jobId: number | string,
  payload: IAIReviewJobResultDocsListPayload
): Promise<IAIReviewJobResultDocsListResponse> => {
  const {data} = await apiClient.post(
    aiReviewEndpoint(`${jobId}/issuecoding/doc-result/view-by-docs`),
    payload
  );
  return data;
};

export const getAIReviewJobResultRelevancyList = async (
  jobId: number | string,
  payload: IAIReviewJobResultRelevancyListPayload
): Promise<IAIReviewJobResultRelevancyListResponse> => {
  const {data} = await apiClient.post(
    aiReviewEndpoint(`${jobId}/relevancy/doc-result`),
    payload
  );
  return normalizeRelevancyDocResultResponse(data);
};

/** AVR-22456: Fetch one Relevancy document summary. */
export const getAIReviewRelevancyDocSummary = async (
  payload: IAIReviewRelevancyDocSummaryRequest
): Promise<IAIReviewRelevancyDocSummary> => {
  const {reviewjobid, reviewJobSubmittedId} = payload;
  const {data} = await apiClient.get<IAIReviewRelevancyDocSummaryResponse>(
    aiReviewEndpoint(`${reviewjobid}/doc-summary/${reviewJobSubmittedId}`)
  );

  return normalizeRelevancyDocSummaryResponse(data);
};

export const getJobList = async (
  payload: IGetJobListPayload
): Promise<IGetJobListResponse> => {
  const {data} = await apiClient.post(aiReviewEndpoint('jobs/list'), payload);
  return {
    ...data,
    jobs: [...data.jobs],
  };
};

export const getGroundTruthEvaluation = async (
  jobId: string | number
): Promise<IGetGroundTruthEvaluationResponse> => {
  const {data} = await apiClient.get(
    aiReviewEndpoint(`jobs/${jobId}/ground-truth-evaluation`)
  );
  return mapGroundTruthEvaluation(data, jobId);
};

export const getGroundTruthTags =
  async (): Promise<IGetGroundTruthTagsResponse> => {
    const {data} = await apiClient.get(aiReviewEndpoint('ground-truth/tags'));
    return data;
  };

export const deleteAIProtocolIntelligence = async (
  payload: IDeleteAIProtocolIntelligencePayload
): Promise<IDeleteAIProtocolIntelligenceResponse> => {
  const {protocolId} = payload;
  const {data} = await apiClient.delete(
    aiReviewEndpoint(`protocol-intelligence/${protocolId}`)
  );
  return data ?? {protocolId};
};

export const getProtocolDownload = async (
  protocolId: string | number | null
): Promise<{
  isSuccess: boolean;
  data?: Blob;
  headers?: any;
  error?: unknown;
}> => {
  const response = await apiClient.get(
    aiReviewEndpoint(`protocol-intelligence/${protocolId}/download`),
    {responseType: 'blob'}
  );
  return {isSuccess: true, data: response.data, headers: response.headers};
};

export const getMasterDocIdErrorDoc = async (
  payload: IGetMasterDocIdForRelevancyPayload
): Promise<IGetMasterDocIdForRelevancyAndDocResponse> => {
  const {jobId, payloadBody} = payload;
  const {data} = await apiClient.post(
    aiReviewEndpoint(`${jobId}/errored/navigate`),
    payloadBody
  );
  return data;
};

export const createNewProtocol = async (
  payload: ICreateNewProtocolPayloadProps
): Promise<ICreateNewProtocolResponse> => {
  const {data} = await apiClient.post<ICreateNewProtocolResponse>(
    aiReviewEndpoint('protocol-intelligence/create'),
    payload
  );
  return data;
};

/** AVR-22438: Save a modified protocol as a new protocol from job results. */
export const saveProtocolAsNew = async (
  payload: ISaveProtocolAsNewPayload
): Promise<ISaveProtocolAsNewResponse> => {
  await apiClient.post(aiReviewEndpoint('protocols/save-as-new'), payload);
};

/**
 * AVR-21735: V2 create protocol with structured caseContext / relevance / issueCoding.
 * Shares create route with V1 save-as-new; payload shape differs.
 */
export const createProtocolIntelligence = async (
  payload: ICreateProtocolIntelligencePayload
): Promise<ICreateProtocolIntelligenceResponse> => {
  const {data} = await apiClient.post<
    ICreateProtocolIntelligenceResponse & {protocolVersionId?: number}
  >(aiReviewEndpoint('protocol-intelligence/create'), payload);
  return {
    protocolId: data.protocolId,
    versionId: data.versionId ?? data.protocolVersionId,
  };
};

/**
 * AVR-21867: Create a new protocol version under an existing protocol.
 * POST /protocol-intelligence/{protocolId}/versions
 */
export const createProtocolVersion = async (
  payload: ICreateProtocolVersionPayload
): Promise<ICreateProtocolVersionResponse> => {
  const {protocolId, ...body} = payload;
  const {data} = await apiClient.post<ICreateProtocolVersionResponse>(
    aiReviewEndpoint(`protocol-intelligence/${protocolId}/versions`),
    {protocolId, ...body}
  );
  return data;
};

export const getMasterDocIdForRelevancy = async (
  payload: IGetMasterDocIdForRelevancyPayload
): Promise<IGetMasterDocIdForRelevancyAndDocResponse> => {
  const {jobId, payloadBody} = payload;
  const {data} = await apiClient.post(
    aiReviewEndpoint(`${jobId}/navigate`),
    payloadBody
  );
  return data;
};

export const getMasterDocIdForIssueCoding = async (
  payload: IGetMasterDocIdForIssueCodingPayload
): Promise<IGetMasterDocIdForIssueCodingResponse> => {
  const {jobId, payloadBody} = payload;
  const {data} = await apiClient.post(
    aiReviewEndpoint(`${jobId}/issuecoding/navigate`),
    payloadBody
  );
  return data;
};

export const getAIReviewJobCloneHistory = async (
  jobId: string | number
): Promise<IGetAIReviewCloneHistoryResponse> => {
  const {data} = await apiClient.get(
    aiReviewEndpoint(`jobs/${jobId}/clone-history`)
  );
  return data;
};
export const getDocListMasterIdForJobDocs = async (
  payload: IGetDocListMasterIdJobDocsPayload
): Promise<IGetMasterDocIdForRelevancyAndDocResponse> => {
  const {reviewJobId, type} = payload;
  const {data} = await apiClient.get(
    aiReviewEndpoint(`${reviewJobId}/navigate/${type}`)
  );
  return data;
};

export const validateProtocolName = async (
  payload: IValidateProtocolNameRequestPayload
): Promise<IValidateProtocolNameResponse> => {
  const {data} = await apiClient.post<IValidateProtocolNameResponse>(
    aiReviewEndpoint('protocol-intelligence/validate-name'),
    payload
  );
  return data;
};

export const updateProtocolName = async ({
  protocolId,
  protocolName,
}: IUpdateProtocolNamePayload): Promise<IUpdateProtocolNameResponse> => {
  try {
    const {data} = await apiClient.put<IUpdateProtocolNameResponse>(
      aiReviewEndpoint(`protocols/${protocolId}`),
      {protocolName}
    );
    return typeof data === 'string' ? data : '';
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'aiReview'
    );
  }
};

export const uploadProtocolV2 = async (
  payload: IUploadProtocolPayload
): Promise<FileUploadResponse> => {
  let requestData: any = payload;
  let headers = {};
  const hasFile = Object.values(payload).some((value) => value instanceof File);
  if (hasFile) {
    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value as any);
      }
    });
    requestData = formData;
    headers = {
      'Content-Type': 'multipart/form-data',
    };
  }
  const {data} = await apiClient.post(
    aiReviewEndpoint('protocol-intelligence/upload'),
    requestData,
    {headers}
  );
  return data;
};

export const getUploadedDataById = async (payload: {
  artifactId: number;
}): Promise<IUploadProtocolResponse> => {
  const {artifactId} = payload;
  const {data = null} = await apiClient.get(
    aiReviewEndpoint(`protocol/${artifactId}`)
  );
  return data;
};
